import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from './Header';
import {
  DollarSign,
  Building,
  Tag,
  PlusCircle,
  Upload,
  CheckCircle,
  AlertCircle,
  Receipt,
  TrendingUp
} from 'lucide-react';

const ModalCreateExpenseForm = () => {
  const server_app_api_php = window.SERVER_APP_API_PHP;
  const [companyID] = useState(1);
  const [businessType, setBusinessType] = useState('');
  const [businessLocations, setBusinessLocations] = useState([]);
  const [businessLocationID, setBusinessLocationID] = useState('');
  const [expense, setExpense] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [recentExpenses, setRecentExpenses] = useState([]);

  useEffect(() => {
    fetchBusinessLocations();
  }, []);

  const fetchBusinessLocations = async () => {
    const formData = new FormData();
    formData.append("apiType", 'getBusinessLocationOnUploadForm');
    formData.append("companyID", companyID);
    
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
      setBusinessLocations(response.data);
    } catch (error) {
      console.error("Error fetching business locations:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    switch (name) {
      case 'businessType':
        setBusinessType(value);
        break;
      case 'business_locationIDCreateExpense':
        setBusinessLocationID(value);
        break;
      case 'expenseCreate':
        setExpense(value);
        break;
      default:
        break;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitStatus({ type: '', message: '' });

    if (!businessLocationID || !expense || !businessType) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Please fill all required fields.' 
      });
      setIsLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append('business_locationIDCreateExpense', businessLocationID);
    formData.append('expenseCreate', expense);
    formData.append('createCompanyID', companyID);
    formData.append('businessType', businessType);
    formData.append('apiType', 'createExpenses');

    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
      
      setSubmitStatus({ 
        type: 'success', 
        message: 'Expense created successfully!' 
      });
      
      // Add to recent expenses
      const selectedLocation = businessLocations.find(loc => 
        loc.businessLocationID === businessLocationID
      );
      
      setRecentExpenses(prev => [{
        id: Date.now(),
        name: expense,
        type: businessType,
        location: selectedLocation?.businessLocationName || 'Unknown',
        date: new Date().toLocaleDateString()
      }, ...prev.slice(0, 4)]);
      
      // Reset form
      setBusinessLocationID('');
      setExpense('');
      setBusinessType('');
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Error: Please check your internet connection. If this persists, contact support.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Header />
      
      <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-16 pl-64">
        <div className="p-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-6">
              <div className="p-3 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl">
                <DollarSign className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Create Expense</h1>
                <p className="text-gray-600 mt-2">Track and manage expenses for your properties</p>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Locations</p>
                    <p className="text-2xl font-bold text-gray-900 mt-2">{businessLocations.length}</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <Building className="w-6 h-6 text-blue-500" />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Recent Expenses</p>
                    <p className="text-2xl font-bold text-gray-900 mt-2">{recentExpenses.length}</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <Receipt className="w-6 h-6 text-green-500" />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Active Today</p>
                    <p className="text-2xl font-bold text-gray-900 mt-2">
                      {recentExpenses.filter(e => e.date === new Date().toLocaleDateString()).length}
                    </p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-purple-500" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Expense Form */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                  {/* Form Header */}
                  <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center space-x-3">
                      <PlusCircle className="w-6 h-6 text-blue-500" />
                      <h2 className="text-xl font-bold text-gray-900">New Expense Entry</h2>
                    </div>
                    <p className="text-gray-600 mt-2">Record a new expense for property management</p>
                  </div>

                  {/* Form Content */}
                  <div className="p-8">
                    {submitStatus.message && (
                      <div className={`mb-6 p-4 rounded-xl ${
                        submitStatus.type === 'success' 
                          ? 'bg-green-50 border border-green-200' 
                          : 'bg-red-50 border border-red-200'
                      }`}>
                        <div className="flex items-center">
                          {submitStatus.type === 'success' ? (
                            <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
                          )}
                          <p className={submitStatus.type === 'success' ? 'text-green-700' : 'text-red-700'}>
                            {submitStatus.message}
                          </p>
                        </div>
                      </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Business Type */}
                      <div>
                        <label htmlFor="businessType" className="block text-sm font-medium text-gray-700 mb-2">
                          <div className="flex items-center">
                            <Tag className="w-4 h-4 mr-2 text-gray-400" />
                            Business Type *
                          </div>
                        </label>
                        <select
                          id="businessType"
                          name="businessType"
                          value={businessType}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                          required
                        >
                          <option value="">Select business type...</option>
                          <option value="Houses">Houses</option>
                          <option value="Plots">Plots</option>
                        </select>
                      </div>

                      {/* Business Location */}
                      <div>
                        <label htmlFor="business_locationIDCreateExpense" className="block text-sm font-medium text-gray-700 mb-2">
                          <div className="flex items-center">
                            <Building className="w-4 h-4 mr-2 text-gray-400" />
                            Business Location *
                          </div>
                        </label>
                        <select
                          id="business_locationIDCreateExpense"
                          name="business_locationIDCreateExpense"
                          value={businessLocationID}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                          required
                        >
                          <option value="">Choose business location...</option>
                          {businessLocations.map(location => (
                            <option key={location.businessLocationID} value={location.businessLocationID}>
                              {location.businessLocationName}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Expense Description */}
                      <div>
                        <label htmlFor="expenseCreate" className="block text-sm font-medium text-gray-700 mb-2">
                          <div className="flex items-center">
                            <DollarSign className="w-4 h-4 mr-2 text-gray-400" />
                            Expense Description *
                          </div>
                        </label>
                        <input
                          type="text"
                          id="expenseCreate"
                          name="expenseCreate"
                          value={expense}
                          onChange={handleInputChange}
                          placeholder="e.g., Maintenance, Utilities, Staff Salary, etc."
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                          required
                        />
                      </div>

                      {/* Submit Button */}
                      <div className="pt-6">
                        <button
                          type="submit"
                          disabled={isLoading || !businessLocationID || !expense || !businessType}
                          className={`w-full py-3.5 px-6 rounded-xl font-medium transition-all duration-300 ${
                            businessLocationID && expense && businessType && !isLoading
                              ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-lg hover:shadow-xl'
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {isLoading ? (
                            <div className="flex items-center justify-center">
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                              Creating Expense...
                            </div>
                          ) : (
                            <div className="flex items-center justify-center">
                              <Upload className="w-5 h-5 mr-2" />
                              Create Expense
                            </div>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>

                  {/* Form Footer */}
                  <div className="px-8 py-6 border-t border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <AlertCircle className="w-4 h-4 text-blue-500" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Important Note</p>
                          <p className="text-xs text-gray-500">Expenses help track operational costs and improve budgeting</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">All fields marked with * are required</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Expenses & Guidelines */}
              <div className="space-y-8">
                {/* Recent Expenses */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <h3 className="font-bold text-gray-900 flex items-center">
                      <Receipt className="w-5 h-5 mr-2 text-blue-500" />
                      Recent Expenses
                    </h3>
                  </div>
                  <div className="p-6">
                    {recentExpenses.length > 0 ? (
                      <div className="space-y-4">
                        {recentExpenses.map(exp => (
                          <div key={exp.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900">{exp.name}</p>
                              <p className="text-xs text-gray-500">{exp.location}</p>
                            </div>
                            <div className="text-right">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                exp.type === 'Houses' 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : 'bg-green-100 text-green-800'
                              }`}>
                                {exp.type}
                              </span>
                              <p className="text-xs text-gray-500 mt-1">{exp.date}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No expenses recorded yet</p>
                        <p className="text-sm text-gray-400 mt-1">Create your first expense above</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Expense Guidelines */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2 text-blue-500" />
                    Expense Guidelines
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-700">Record all operational expenses for accurate accounting</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-700">Categorize expenses by property type (Houses/Plots)</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-700">Regular expense tracking improves budgeting accuracy</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default ModalCreateExpenseForm;